import { cacheKeys } from "@constants/cache-keys";
import { isScreenshotMode } from "@constants/common";
import { mockedLeaderboard } from "@constants/mocked-data";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import { useAppStore } from "@hooks/useAppStore";
import type { LeaderboardTimePeriod } from "@memorang/ui/components/Leaderboard/types";
import { useQuery } from "@tanstack/react-query";
import { useCallback } from "react";
import { fetchLeaderboardItems } from "../api/queries";
import { useUserLeaderboard } from "./useUserLeaderboard";

type Options = {
	timePeriod: LeaderboardTimePeriod;
};

export const useLeaderboard = ({ timePeriod }: Options) => {
	const limit = useAppStore(
		(state) => state.app.leaderboards.numUsersToDisplay,
	);
	const {
		currentExamContext: { projectId, organizationId },
		isExamContextLoading,
	} = useExamContext();

	const leaderboardItemsResult = useQuery({
		queryKey: [
			cacheKeys.leaderboard,
			projectId,
			organizationId,
			timePeriod,
			limit,
		],
		queryFn: async () => {
			const items = isScreenshotMode
				? mockedLeaderboard[timePeriod]
				: await fetchLeaderboardItems({
						periodType: timePeriod,
						projectId,
						organizationId,
						limit,
					});

			return items.map((item, index) => ({
				rank: index + 1,
				...item,
			}));
		},
		enabled: !!projectId && !!organizationId,
	});

	const userLeaderboardItemResult = useUserLeaderboard({ timePeriod });

	const refetch = useCallback(() => {
		leaderboardItemsResult.refetch();
		userLeaderboardItemResult.refetch();
	}, [leaderboardItemsResult.refetch, userLeaderboardItemResult.refetch]);

	return {
		leaderboardItems: leaderboardItemsResult.data ?? [],
		userLeaderboardItem: userLeaderboardItemResult.data,
		isLoading:
			isExamContextLoading ||
			leaderboardItemsResult.isLoading ||
			userLeaderboardItemResult.isLoading,
		isError: leaderboardItemsResult.isError,
		error: leaderboardItemsResult.error,
		refetch,
	};
};
